import { ChevronDown } from 'lucide-react';
import { useMemo, useState } from 'react';

import { COLORS } from './constants';

import type { Issue } from '@/models/Issue';

import { VALUE_FONT_SMALL } from '@/theme/consts';

interface InspectionDiffCardProps {
  issues: Issue[];
}

const URGENCY_ORDER = ['Critical', 'Urgent', 'Moderate', 'Low', 'Monitor'];
const URGENCY_COLOR: Record<string, string> = {
  Critical: '#DB5282', Urgent: '#FF9A4D', Moderate: '#FFF981',
  Low: '#5AE9FF', Monitor: '#9A938A',
};
const sortByUrgency = (a: Issue, b: Issue) =>
  URGENCY_ORDER.indexOf(a.urgency) - URGENCY_ORDER.indexOf(b.urgency);

const fmtCost = (min: number, max: number) => {
  if (!min && !max) return null;
  const f = (n: number) => n >= 1000 ? `$${Math.round(n / 1000)}k` : `$${n}`;
  return min === max ? f(min) : `${f(min)}–${f(max)}`;
};

const sumCost = (issues: Issue[]) =>
  issues.reduce((sum, i) => sum + (((i.costEstimateMin ?? 0) + (i.costEstimateMax ?? 0)) / 2), 0);

const fmtSummaryCost = (n: number) => {
  if (n <= 0) return null;
  return n >= 1000 ? `$${(n / 1000).toFixed(1).replace(/\.0$/, '')}k` : `$${Math.round(n)}`;
};

const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

const formatCategory = (cat: string) =>
  cat.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

interface IssueCardProps {
  issue: Issue;
}

const IssueCard = ({ issue }: IssueCardProps) => {
  const [expanded, setExpanded] = useState(false);
  const [hovered, setHovered] = useState(false);
  const cost = fmtCost(issue.costEstimateMin ?? 0, issue.costEstimateMax ?? 0);
  const urgencyColor = URGENCY_COLOR[issue.urgency] ?? COLORS.textDim;
  const hasDescription = !!issue.description?.trim();

  return (
    <div style={{ marginBottom: 6 }}>
      <div
        onClick={() => hasDescription && setExpanded((v) => !v)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 14px',
          background: expanded || hovered ? 'var(--tx-fill)' : 'var(--tx-glass-panel)',
          borderRadius: expanded && hasDescription ? '10px 10px 0 0' : 10,
          cursor: hasDescription ? 'pointer' : 'default',
          transition: 'background 0.15s',
        }}>
        <span style={{ width: 3, height: 24, borderRadius: 2, background: urgencyColor, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.text }}>{issue.title}</div>
          <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 2 }}>{issue.urgency}</div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0, gap: 2 }}>
          {cost && <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.text, whiteSpace: 'nowrap' }}>{cost}</span>}
          {hasDescription && (
            <span style={{
              color: COLORS.textDim, display: 'inline-flex', alignItems: 'center',
              transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s',
            }}>
              <ChevronDown size={14} />
            </span>
          )}
        </div>
      </div>
      {expanded && hasDescription && (
        <div style={{
          background: 'var(--tx-glass-panel)', padding: '12px 14px 14px 26px',
          borderRadius: '0 0 10px 10px',
          fontSize: 12, color: COLORS.text, lineHeight: 1.5,
        }}>
          {issue.description}
        </div>
      )}
    </div>
  );
};

const VISIBLE_REMARKS = 6;
const REMARK_ROW_HEIGHT = 56;

const RemarkList = ({ issues }: { issues: Issue[] }) => (
  <div
    className="inspection-diff-scroll"
    style={{
      maxHeight: issues.length > VISIBLE_REMARKS ? REMARK_ROW_HEIGHT * VISIBLE_REMARKS : undefined,
      overflowY: issues.length > VISIBLE_REMARKS ? 'auto' : undefined,
      paddingRight: issues.length > VISIBLE_REMARKS ? 6 : undefined,
    }}>
    {issues.length > 0 ? (
      issues.map((issue) => <IssueCard key={issue.id} issue={issue} />)
    ) : (
      <div style={{ fontSize: 11, color: COLORS.textDim, fontStyle: 'italic' }}>No findings.</div>
    )}
  </div>
);

export const InspectionDiffCard = ({ issues }: InspectionDiffCardProps) => {
  const isMobile = window.innerWidth < 900;
  const sellerIssues = useMemo(() => issues.filter((i) => i.source === 'seller'), [issues]);
  const buyerIssues  = useMemo(() => issues.filter((i) => i.source === 'buyer'),  [issues]);

  const hasSeller = sellerIssues.length > 0;
  const hasBuyer  = buyerIssues.length > 0;

  const sellerTitles = useMemo(() => new Set(sellerIssues.map((i) => normalize(i.title))), [sellerIssues]);
  const buyerTitles  = useMemo(() => new Set(buyerIssues.map((i)  => normalize(i.title))), [buyerIssues]);

  // Only the findings unique to one side are worth reviewing — anything both
  // sides already agree on isn't a discrepancy.
  const sellerUnique = useMemo(
    () => sellerIssues.filter((i) => !buyerTitles.has(normalize(i.title))),
    [sellerIssues, buyerTitles],
  );
  const buyerUnique = useMemo(
    () => buyerIssues.filter((i) => !sellerTitles.has(normalize(i.title))),
    [buyerIssues, sellerTitles],
  );

  const categories = useMemo(() => {
    const cats = new Set([...sellerUnique, ...buyerUnique].map((i) => i.category));
    return Array.from(cats)
      .map((cat) => ({
        cat,
        seller: sellerUnique.filter((i) => i.category === cat).sort(sortByUrgency),
        buyer: buyerUnique.filter((i) => i.category === cat).sort(sortByUrgency),
      }))
      .sort((a, b) => (b.seller.length + b.buyer.length) - (a.seller.length + a.buyer.length));
  }, [sellerUnique, buyerUnique]);

  const summary = useMemo(() => {
    const topByUrgency = (list: Issue[]) => [...list].sort(sortByUrgency)[0];
    return {
      sellerCost: sumCost(sellerUnique),
      buyerCost: sumCost(buyerUnique),
      topSeller: topByUrgency(sellerUnique),
      topBuyer: topByUrgency(buyerUnique),
      sellerOnlyCats: categories.filter((c) => c.seller.length > 0 && c.buyer.length === 0).map((c) => formatCategory(c.cat)),
      buyerOnlyCats: categories.filter((c) => c.buyer.length > 0 && c.seller.length === 0).map((c) => formatCategory(c.cat)),
    };
  }, [sellerUnique, buyerUnique, categories]);

  const [showDetails, setShowDetails] = useState(false);

  if (!hasSeller || !hasBuyer) return null;

  return (
    <div style={{
      background: COLORS.card, borderRadius: 25,
      backgroundImage: 'var(--tx-surface-light)', boxShadow: 'var(--tx-shadow)', padding: '20px',
    }}>
      <style>{`
        .inspection-diff-scroll::-webkit-scrollbar { width: 4px; }
        .inspection-diff-scroll::-webkit-scrollbar-thumb { background-color: rgba(255,255,255,0.12); border-radius: 2px; }
      `}</style>
      {/* Title */}
      <div style={{ fontSize: 15, fontWeight: 300, color: COLORS.text, marginBottom: 14 }}>
        Report comparison
      </div>

      {/* Narrative summary tiles — replace the old stat/donut cards */}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 10, marginBottom: 4 }}>
        <div style={{
          flex: 1, background: '#2a2a2a', borderRadius: 16, padding: '16px',
        }}>
          <div style={{
            fontSize: 10, fontWeight: 600, color: COLORS.textDim, letterSpacing: '0.06em',
marginBottom: 12,
          }}>
            Seller disclosed
          </div>
          <div style={{ display: 'flex', gap: 20, marginBottom: 12 }}>
            <div>
              <div
                style={{
                  fontFamily: VALUE_FONT_SMALL,
                  fontSize: 24,
                  fontWeight: 600,
                  letterSpacing: 0,
                  color: COLORS.text,
                  lineHeight: 1,
                }}>
                {sellerUnique.length}
              </div>
              <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 4 }}>Unconfirmed issues</div>
            </div>
            <div>
              <div style={{ fontFamily: VALUE_FONT_SMALL, fontSize: 24, fontWeight: 600, letterSpacing: 0, color: COLORS.text, lineHeight: 1 }}>
                {fmtSummaryCost(summary.sellerCost) ?? '—'}
              </div>
              <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 4 }}>Est. cost</div>
            </div>
          </div>
          <div style={{ fontSize: 13, color: COLORS.text, lineHeight: 1.6 }}>
            Your inspector didn&apos;t independently confirm these
            {summary.topSeller && <>, most urgently <b>{summary.topSeller.title}</b></>}.
            {summary.sellerOnlyCats.length > 0 && (
              <> Nothing was confirmed in {summary.sellerOnlyCats.join(' or ')}.</>
            )}
          </div>
        </div>
        <div style={{
          flex: 1, background: '#2a2a2a', borderRadius: 16, padding: '16px',
        }}>
          <div style={{
            fontSize: 10, fontWeight: 600, color: COLORS.textDim, letterSpacing: '0.06em',
marginBottom: 12,
          }}>
            Your inspector found
          </div>
          <div style={{ display: 'flex', gap: 20, marginBottom: 12 }}>
            <div>
              <div
                style={{
                  fontFamily: VALUE_FONT_SMALL,
                  fontSize: 24,
                  fontWeight: 600,
                  letterSpacing: 0,
                  color: COLORS.text,
                  lineHeight: 1,
                }}>
                {buyerUnique.length}
              </div>
              <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 4 }}>Undisclosed issues</div>
            </div>
            <div>
              <div style={{ fontFamily: VALUE_FONT_SMALL, fontSize: 24, fontWeight: 600, letterSpacing: 0, color: COLORS.text, lineHeight: 1 }}>
                {fmtSummaryCost(summary.buyerCost) ?? '—'}
              </div>
              <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 4 }}>Est. cost</div>
            </div>
          </div>
          <div style={{ fontSize: 13, color: COLORS.text, lineHeight: 1.6 }}>
            The seller didn&apos;t disclose these
            {summary.topBuyer && <>, most urgently <b>{summary.topBuyer.title}</b></>}.
            {summary.buyerOnlyCats.length > 0 && (
              <> Nothing was disclosed in {summary.buyerOnlyCats.join(' or ')}.</>
            )}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={() => setShowDetails((v) => !v)}
          style={{
            display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none',
            color: COLORS.textDim, fontSize: 11, fontWeight: 600, cursor: 'pointer', padding: 0,
            letterSpacing: '0.04em', marginTop: 14,
          }}>
          {showDetails ? 'Hide' : 'Show'} details
          <ChevronDown size={12} style={{
            transform: showDetails ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s',
          }} />
        </button>
      </div>

      {showDetails && (
        <div style={{ marginTop: 16 }}>
          {/* Category-grouped comparison */}
          {categories.map(({ cat, seller, buyer }) => (
            <div key={cat} style={{ marginTop: 16 }}>
              <div style={{
                fontSize: 10, fontWeight: 600, color: COLORS.textDim, letterSpacing: '0.06em',
paddingBottom: 8,
                borderBottom: `1px solid ${COLORS.border}`, marginBottom: 8,
              }}>
                {formatCategory(cat)}
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                gap: isMobile ? 24 : 20,
              }}>
                <RemarkList issues={seller} />
                <RemarkList issues={buyer} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default InspectionDiffCard;
